import { EVIDENCE_VAULT_WARN_RATIO } from '../../lib/evidenceVault.js';
import { PIPELINE_NOTIFICATIONS } from '../../lib/settingsStore.js';

const LOCKER_WARN_PERCENT = Math.round(EVIDENCE_VAULT_WARN_RATIO * 100);

export default function NotificationsPanel({
  notifications,
  onChange,
  onPipelineChange,
  onSubmit,
  pending,
  shareMessages = false,
  canManageSharing = false,
  onShareMessages,
  saveNote = null,
}) {
  return (
    <form
      className="settings-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <h2>Notifications</h2>
      <p className="settings-lead">
        Choose which email and phone alerts you receive for account and case activity.
      </p>

      <label className="toggle-row">
        <input
          type="checkbox"
          checked={Boolean(notifications.emailEnabled)}
          onChange={(e) => onChange({ emailEnabled: e.target.checked })}
        />
        <span>
          <strong>Enable email notifications</strong>
          <small>Master switch for optional email alerts. Payment failure is always sent.</small>
        </span>
      </label>

      <div className="settings-subsection">
        <h3>Account alerts</h3>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.monthlyBillingStatement)}
            onChange={(e) => onChange({ monthlyBillingStatement: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>Monthly billing statement</strong>
            <small>Summary of platform charges and case collection fees.</small>
          </span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.newSignInAlert)}
            onChange={(e) => onChange({ newSignInAlert: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>New sign-in to account alert</strong>
            <small>Notify when a new browser or device signs in.</small>
          </span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.thirdPartyAppAlert)}
            onChange={(e) => onChange({ thirdPartyAppAlert: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>Third-party app access alert</strong>
            <small>Notify when QuickBooks, Zoho Books, or another app connects.</small>
          </span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.integrationSyncAlert)}
            onChange={(e) => onChange({ integrationSyncAlert: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>Successful integration sync</strong>
            <small>
              Notify when QuickBooks, Zoho Books, or another app finishes importing invoices.
            </small>
          </span>
        </label>
      </div>

      <div className="settings-subsection">
        <h3>Pipeline status notifications</h3>
        <p className="settings-sublead">
          Case progress alerts such as attorney assignment, mailing, debtor response, and payment.
        </p>
        {PIPELINE_NOTIFICATIONS.map((item) => (
          <label key={item.id} className="toggle-row">
            <input
              type="checkbox"
              checked={Boolean(notifications.pipeline?.[item.id])}
              onChange={(e) => onPipelineChange(item.id, e.target.checked)}
              disabled={!notifications.emailEnabled}
            />
            <span>
              <strong>{item.label}</strong>
              <small>{item.description}</small>
            </span>
          </label>
        ))}
      </div>

      <div className="settings-subsection">
        <h3>Messages</h3>
        <p className="settings-sublead">
          Alerts when a message is addressed to you. An owner or admin can also share threads across the organization.
        </p>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(shareMessages)}
            disabled={!canManageSharing || pending}
            onChange={(e) => onShareMessages?.(e.target.checked)}
          />
          <span>
            <strong>Share organization messages</strong>
            <small>Everyone in the organization can read messages on a demand.</small>
          </span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.newMessage)}
            onChange={(e) => onChange({ newMessage: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>New message</strong>
            <small>Alert when a message is addressed to you.</small>
          </span>
        </label>
      </div>

      <div className="settings-subsection">
        <h3>Notes</h3>
        <p className="settings-sublead">
          Alerts when a teammate adds a note on a shared demand or debtor.
        </p>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.newNote)}
            onChange={(e) => onChange({ newNote: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>New note</strong>
            <small>A teammate added a note on a demand or debtor.</small>
          </span>
        </label>
      </div>

      <div className="settings-subsection">
        <h3>Payments</h3>
        <div className="toggle-row">
          <span>
            <strong>Payment failure</strong>
            <small>Always sent when a payment method fails or a charge is declined.</small>
          </span>
        </div>
      </div>

      <div className="settings-subsection">
        <h3>Evidence Locker</h3>
        <p className="settings-sublead">
          Alerts when locker storage is getting close to your plan limit.
        </p>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(notifications.evidenceLockerNearingLimit)}
            onChange={(e) => onChange({ evidenceLockerNearingLimit: e.target.checked })}
            disabled={!notifications.emailEnabled}
          />
          <span>
            <strong>Approaching storage limit</strong>
            <small>
              Alert when Evidence Locker reaches {LOCKER_WARN_PERCENT}% of your plan, before
              uploads are blocked.
            </small>
          </span>
        </label>
      </div>

      <div className="drawer-actions notifications-save">
        {saveNote?.text ? (
          <p
            className={
              saveNote.error
                ? 'form-error notifications-save-note'
                : 'form-info notifications-save-note'
            }
            role="status"
          >
            {saveNote.text}
          </p>
        ) : null}
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending ? 'Saving…' : 'Save notifications'}
        </button>
      </div>
    </form>
  );
}
